import { useLiveQuery } from 'dexie-react-hooks';
import { closeDayAdvice, historyAdvice, topAdvice } from '@/engine/advice';
import { balanceAdvice, pushPullSets, weeklySetsByMuscle } from '@/engine/balance';
import { closeDay, type CloseDayCandidate } from '@/engine/close-day';
import { coverageFor } from '@/engine/coverage';
import { computeDeficits } from '@/engine/deficits';
import { CLOSE_DAY, HISTORY } from '@/engine/config';
import { addDays, daysBetween } from '@/engine/dates';
import { nutrientHistory, type NutrientDay } from '@/engine/history';
import { NUTRIENTS } from '@/engine/nutrients';
import { scaleNutrients, sumNutrients } from '@/engine/portions';
import { progressionAdvice } from '@/engine/progression';
import { redundancyAdvice, type BestSession } from '@/engine/redundancy';
import { isEffectiveSet, volumeKg } from '@/engine/oneRm';
import type { Advice, Exercise, NutrientKey, Profile, Targets } from '@/engine/types';
import { db, type FoodRow, type MealRow, type WorkoutRow } from '@/lib/db';
import { mergeExercises } from '@/features/workout/useWorkout';
import { excludedByPreferences } from '@/lib/preferences';
import { CONCENTRATE } from '@/lib/search';

/** Ventana MÍNIMA de historial. Se amplía si hacen falta más días válidos. */
const HISTORY_DAYS = 45;
const TRAINING_DAYS = 28;

/**
 * Familias que NO se comen crudas. Proponer "300 g de alubias crudas" es un
 * consejo inservible, por muy bien que cuadren los números.
 */
const COOK_REQUIRED = ['legumbre', 'cereal', 'carne', 'cerdo'];

/**
 * Etiquetas de cosas que se comen en plato. Deja fuera bebidas, dulces y
 * alcohol: proponerlos para cubrir un hueco nutricional no es un consejo sano.
 */
const FOOD_TAGS = [
  'carne',
  'cerdo',
  'pescado',
  'marisco',
  'lacteo',
  'huevo',
  'cereal',
  'legumbre',
  'fruta',
  'verdura',
  'fruto_seco',
  'soja',
];

/**
 * Porción máxima realista por familia, en gramos.
 *
 * Son cantidades de plato, no límites nutricionales: 300 g de chalotas cuadran
 * los números tan bien como 40 g, pero solo una de las dos se come. La porción
 * doméstica del propio alimento manda sobre esta tabla cuando existe.
 */
const MAX_PORTION_BY_TAG: Record<string, number> = {
  grasa: 30,
  fruto_seco: 50,
  cereal: 125,
  legumbre: 200,
  huevo: 150,
  carne: 200,
  cerdo: 150,
  pescado: 200,
  marisco: 200,
  lacteo: 250,
  soja: 200,
  fruta: 250,
  verdura: 250,
};

/**
 * Aromáticos y condimentos: se usan a cucharadas, no a puñados. Cuadran
 * cualquier déficit de micros y arruinan la propuesta.
 */
const AROMATIC = /^(ajo|cebolla|chalota|puerro|perejil|cebollino|jengibre|laurel|especia)/i;

/** Ingredientes de cocina, no platos: harinas, sémolas, salvados, almidones. */
const INGREDIENT = /^(harina|sémola|semola|almidón|almidon|salvado|germen|levadura|gluten)/i;

/** Lo que el propio nombre declara sin preparar. */
const RAW_NAME = /\bcrud[oa]s?\b|\bsec[oa]s?\b|\bsin preparar\b/i;

/** Lo que el nombre declara ya listo para comer. */
const PREPARED_NAME =
  /cocid|hervid|asad|al horno|horneado|en conserva|frit|tostad|guisad|estofad|al vapor|a la plancha|curad|ahumad|\bpan\b|\bbagel|\bpasta\b|galleta/i;

/**
 * Densidad de un grano o una legumbre SECOS, en kcal/100 g. Por encima de esto
 * un cereal o una legumbre está sin cocer: cocidos rondan las 100-150 kcal, y
 * el pan, que sí se come tal cual, se queda en 250-280.
 */
const DRY_STAPLE_KCAL = 300;

export interface AdviceData {
  /** Avisos permanentes: tendencia histórica y entrenamiento. */
  advice: Advice[];
  /**
   * Propuesta de cierre del día. Se calcula siempre pero se entrega aparte
   * porque el spec la define como acción BAJO DEMANDA (SPEC 6.C): enseñarla
   * cuando alguien lleva 300 de 2600 kcal no es cerrar el día, es ruido.
   */
  closing: Advice | null;
  loading: boolean;
}

/**
 * Desde qué día hay que leer el historial.
 *
 * Las ventanas del spec se cuentan en días VÁLIDOS (completos), no en días de
 * calendario: quien marca completo uno de cada tres días necesita 90 días de
 * calendario para tener los 30 válidos de la regla de silencio. Con la ventana
 * fija de 45 días, esa regla no llegaba a cumplirse nunca y la media
 * exponencial salía recortada (R38).
 *
 * Nunca se lee menos que la ventana mínima: a quien registra a diario, 30 días
 * válidos son 30 de calendario, y la exponencial quiere algo más de recorrido.
 */
export function historyWindowStart(date: string, completeDates: readonly string[]): string {
  const floor = addDays(date, -HISTORY_DAYS);
  const nth = [...completeDates].sort((a, b) => b.localeCompare(a))[HISTORY.silenceWindowDays - 1];
  return nth != null && nth < floor ? nth : floor;
}

/**
 * Reúne todo lo que el motor necesita y le pide las recomendaciones.
 *
 * Este hook es el único sitio que sabe a la vez de Dexie y del motor: el motor
 * no consulta nada, recibe. Así se puede testear sin navegador (SPEC 3.1).
 */
export function useAdvice(
  date: string,
  profile: Profile | null,
  targets: Targets | null,
): AdviceData {
  const data = useLiveQuery(async () => {
    const days = await db.days
      .where('date')
      .between(addDays(date, -HISTORY.maxLookbackDays), date, true, true)
      .toArray();

    const from = historyWindowStart(
      date,
      days.filter((d) => d.complete).map((d) => d.date),
    );
    const meals = (await db.meals.where('date').between(from, date, true, true).toArray()).filter(
      (m) => m.deletedAt === null,
    );
    const workouts = (
      await db.workouts
        .where('date')
        .between(addDays(date, -TRAINING_DAYS), date, true, true)
        .toArray()
    ).filter((w) => w.deletedAt === null);
    const foods = (await db.foods.toArray()).filter((f) => f.deletedAt === null);
    // Los ejercicios propios cuentan igual que los del catálogo: si el motor no
    // los conoce, no progresan ni suman volumen por músculo.
    const exercises = mergeExercises(await db.exercises.toArray());

    return { meals, days, workouts, foods, exercises };
  }, [date]);

  if (!data || !profile || !targets) {
    return { advice: [], closing: null, loading: data == null };
  }

  const diet = dietAdvice(date, data.meals, data.days, data.foods, profile, targets);
  const advice = [...diet.history, ...trainingAdvice(date, data.workouts, data.exercises)];

  return { advice: topAdvice(advice), closing: diet.closing, loading: false };
}

// --- Dieta ---

function dietAdvice(
  date: string,
  meals: MealRow[],
  days: { date: string; complete: boolean; targets?: Targets | null }[],
  foods: FoodRow[],
  profile: Profile,
  targets: Targets,
): { history: Advice[]; closing: Advice | null } {
  const today = meals.filter((m) => m.date === date);
  const todayTotals = sumNutrients(today.map((m) => scaleNutrients(m.per100g, m.grams)));
  const completeDates = new Set(days.filter((d) => d.complete).map((d) => d.date));
  const deficits = computeDeficits({ date, meals, completeDates, targets });

  const out: Advice[] = [];

  const remainingKcal = Math.max(targets.kcal - (todayTotals.kcal ?? 0), 0);
  let closing: Advice | null = null;

  if (deficits.length > 0 && remainingKcal > 0) {
    const result = closeDay({
      deficits,
      candidates: pickCandidates(foods, meals, profile),
      remainingKcal,
      upperLimits: targets.upperLimits,
      currentTotals: todayTotals,
    });
    closing = result
      ? closeDayAdvice(result, deficits, { hideCalories: profile.hideCalories })
      : null;
  }

  // Tendencia histórica, solo de los micros (los macros se juzgan por día).
  for (const meta of NUTRIENTS) {
    if (meta.window !== 'semana') continue;
    const target = targets.micros[meta.key];
    if (!target) continue;

    const history = buildHistory(meta.key, meals, days, target, date);
    const advice = historyAdvice(meta.key, nutrientHistory(history, date));
    if (advice) out.push(advice);
  }

  return { history: out, closing };
}

/**
 * El ratio de cada día se calcula con el objetivo que estaba VIGENTE ese día,
 * congelado al marcarlo completo. Con el objetivo de hoy, cambiar de
 * «mantener» a «perder» reescribía el pasado: meses de días correctos pasaban
 * a contar como excesos. Los días cerrados antes de que se guardara el
 * objetivo se juzgan con el de hoy, que es lo único que se sabe de ellos.
 */
function buildHistory(
  key: NutrientKey,
  meals: MealRow[],
  days: { date: string; complete: boolean; targets?: Targets | null }[],
  target: number,
  date: string,
): NutrientDay[] {
  const dayByDate = new Map(days.map((d) => [d.date, d]));
  const byDate = new Map<string, MealRow[]>();
  for (const meal of meals) {
    const list = byDate.get(meal.date) ?? [];
    list.push(meal);
    byDate.set(meal.date, list);
  }

  const out: NutrientDay[] = [];
  for (const [day, rows] of byDate) {
    if (daysBetween(day, date) < 0) continue;
    const record = dayByDate.get(day);
    const thatDay = record?.targets?.micros[key] ?? target;
    const totals = sumNutrients(rows.map((m) => scaleNutrients(m.per100g, m.grams)));
    out.push({
      date: day,
      complete: record?.complete ?? false,
      ratio: (totals[key] ?? 0) / thatDay,
      coverage: coverageFor(rows, key),
    });
  }

  return out;
}

/**
 * Candidatos para el cierre: genéricos etiquetados, con los que el usuario ya
 * come marcados como familiares para que el motor los prefiera.
 *
 * Exportado para poder probarlo: decide QUÉ puede proponerse para cerrar el
 * día, que es la mitad de la calidad del consejo.
 */
export function pickCandidates(
  foods: FoodRow[],
  meals: { foodId: string }[],
  profile: Profile,
): CloseDayCandidate[] {
  const eaten = new Set(meals.map((m) => m.foodId));

  return (
    foods
      .filter((f) => f.kind === 'generico' && (f.per100g.kcal ?? 0) > 0)
      // Vegetariano, alergias y lo que no come (SPEC 7.1.3), por nombre y familia.
      .filter((f) => !excludedByPreferences(f, profile))
      .filter((f) => f.tags.some((tag) => FOOD_TAGS.includes(tag)))
      // Café soluble, cacao en polvo, algas secas: los que más micros tienen por
      // 100 g, así que el motor los elegía siempre ("300 g de café
      // instantáneo"). Son condimentos, no alimentos con porción.
      .filter((f) => !CONCENTRATE.test(f.name))
      .filter((f) => !AROMATIC.test(f.name))
      .filter((f) => !INGREDIENT.test(f.name))
      .filter((f) => isEdibleAsIs(f))
      // Lo que la persona YA come va primero, pase lo que pase: el corte a 150
      // se hacía por lo corriente del nombre, así que sus propios alimentos se
      // quedaban fuera de la lista antes de que el motor pudiera preferirlos.
      //
      // Después, lo CORRIENTE que es el alimento, no lo completo que esté su
      // ficha: ordenar por nutrientes medidos premiaba rarezas de laboratorio
      // —salía "caribú, cocido, asado"— porque son justo las que USDA analiza a
      // fondo. Un nombre corto delata al alimento genérico.
      .sort(
        (a, b) =>
          Number(eaten.has(b.id)) - Number(eaten.has(a.id)) ||
          plainness(a) - plainness(b) ||
          knownCount(b) - knownCount(a) ||
          a.name.localeCompare(b.name, 'es'),
      )
      .slice(0, CLOSE_DAY.maxCandidates)
      .map((f) => ({
        id: f.id,
        name: f.name,
        per100g: f.per100g,
        tags: f.tags,
        familiar: eaten.has(f.id),
        maxGrams: maxPortionOf(f),
      }))
  );
}

/** Descarta lo que está crudo y hay que cocinar antes de comer. */
function isEdibleAsIs(food: FoodRow): boolean {
  if (!food.tags.some((tag) => COOK_REQUIRED.includes(tag))) return true;
  if (RAW_NAME.test(food.name)) return false;
  // La espelta y la avena en grano no dicen "crudas" en ningún sitio, y el
  // motor proponía "150 g de espelta" (338 kcal/100 g) como si se comiera a
  // cucharadas: con densidad de grano seco y sin preparación en el nombre, está
  // cruda lo diga o no.
  return (food.per100g.kcal ?? 0) <= DRY_STAPLE_KCAL || PREPARED_NAME.test(food.name);
}

/** Tope de porción: el menor de los que apliquen por etiqueta. */
function maxPortionOf(food: FoodRow): number | undefined {
  const caps = food.tags
    .map((tag) => MAX_PORTION_BY_TAG[tag])
    .filter((v): v is number => v != null);
  return caps.length > 0 ? Math.min(...caps) : undefined;
}

/** Cuanto más bajo, más genérico y habitual es el alimento. */
function plainness(food: FoodRow): number {
  const segments = food.name.split(',').length;
  return segments * 10 + Math.min(food.name.length, 60) / 10;
}

function knownCount(food: FoodRow): number {
  return Object.values(food.per100g).filter((v) => v != null).length;
}

// --- Entrenamiento ---

function trainingAdvice(
  date: string,
  workouts: WorkoutRow[],
  exercises: readonly Exercise[],
): Advice[] {
  const catalog = new Map(exercises.map((e) => [e.id, e]));
  const out: Advice[] = [];

  const sorted = [...workouts].sort((a, b) => a.date.localeCompare(b.date));
  const todayWorkout = sorted.find((w) => w.date === date);

  // Progresión: una por ejercicio de la sesión de hoy.
  for (const exerciseId of todayWorkout?.exerciseOrder ?? []) {
    const exercise = catalog.get(exerciseId);
    if (!exercise) continue;

    const sessions = sorted
      .filter((w) => w.sets.some((s) => s.exerciseId === exerciseId))
      .map((w) => ({ date: w.date, sets: w.sets }));

    const advice = progressionAdvice(exercise, sessions);
    if (advice) out.push(advice);
  }

  // Redundancia dentro de la sesión de hoy.
  if (todayWorkout) {
    // El mejor día de cada ejercicio, con SU número de series: la regla compara
    // kg por serie, no totales de sesión (si no, avisaría a mitad de sesión).
    const best = new Map<string, BestSession>();
    for (const workout of sorted) {
      if (workout.date === date) continue;
      for (const exerciseId of workout.exerciseOrder) {
        const sets = workout.sets.filter((s) => s.exerciseId === exerciseId && isEffectiveSet(s));
        if (sets.length === 0) continue;
        const volume = volumeKg(sets);
        if (volume > (best.get(exerciseId)?.volumeKg ?? 0)) {
          best.set(exerciseId, { volumeKg: volume, effectiveSets: sets.length });
        }
      }
    }
    out.push(...redundancyAdvice(todayWorkout, catalog, best));
  }

  // Descompensación: la semana que termina hoy.
  const weekSets = sorted
    .filter((w) => daysBetween(w.date, date) >= 0 && daysBetween(w.date, date) < 7)
    .flatMap((w) => w.sets);

  if (weekSets.length > 0) {
    const doing = [...new Set(weekSets.map((s) => s.exerciseId))];
    out.push(
      ...balanceAdvice(
        weeklySetsByMuscle(weekSets, catalog),
        exercises,
        doing,
        pushPullSets(weekSets, catalog),
      ),
    );
  }

  return out;
}
